import type { ReactNode } from "react";
import { Skeleton } from "@baluu/ui/components/skeleton";
import { Button } from "@baluu/ui/components/button";

// What every view shows while it loads, when it can't, and after an action.
// No modal dialogs here: a dialog portals to the document, outside the
// element's shadow root and its styles, so confirmations open in place.

export function BookingLoading({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-3" aria-busy="true" aria-label="Loading">
      <Skeleton className="h-8 w-2/3 rounded-lg" />
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-20 w-full rounded-lg" />
      ))}
    </div>
  );
}

/** A page-level problem: the booking can't be found or loaded, or the
 * link is wrong. */
export function BookingProblem({
  title,
  message,
  onRetry,
  action,
}: {
  title: string;
  message?: string;
  onRetry?: () => void;
  action?: ReactNode;
}) {
  return (
    <div role="alert" className="mx-auto max-w-lg space-y-3 rounded-lg border bg-muted/30 p-8 text-center">
      <h1 className="text-lg font-semibold">{title}</h1>
      {message && <p className="text-sm text-muted-foreground">{message}</p>}
      <div className="flex flex-wrap justify-center gap-2">
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry}>
            Try again
          </Button>
        )}
        {action}
      </div>
    </div>
  );
}

/** An outcome line inside a view (an action went through, or didn't). */
export function Notice({ tone, children }: { tone: "success" | "error" | "info"; children: ReactNode }) {
  const tones = {
    success: "border-green-200 bg-green-50 text-green-900",
    error: "border-red-200 bg-red-50 text-red-900",
    info: "border-border bg-muted/40 text-foreground",
  } as const;
  return (
    <div role={tone === "error" ? "alert" : "status"} className={`rounded-lg border p-4 text-sm ${tones[tone]}`}>
      {children}
    </div>
  );
}

/** The record isn't this site's (or doesn't exist): the same words either
 * way, so another school's ids reveal nothing. */
export function RecordNotFound({ what = "booking" }: { what?: "booking" | "bundle" | "gift card" | "purchase" | "payment" | "proposal" }) {
  return <BookingProblem title={`We couldn’t find this ${what}`} message={`Please open the link from your ${what} email again.`} />;
}
